import type { LucideProps } from 'lucide-react';
import type { PropsWithChildren } from 'react';
import { createStateContext, useToggle } from 'react-use';
import { Button } from '../../components/ui/button.tsx';
import { cn } from '../../lib/utils.ts';
import { Dialog } from './dialog';
import { DialogStateManager, useDialog } from './dialog-provider';
import type { DialogInnerProps } from './types';

type Focus = 'submit' | 'destroy';

const [useConfirmationDialog, ConfirmationDialogProvider] = createStateContext<{ focus: string }>({
    focus: 'submit',
});

type BaseConfirmationRejectButtonProps = {
    onClick: () => void;
} & PropsWithChildren;

const BaseConfirmationRejectButton = ({ children, onClick }: BaseConfirmationRejectButtonProps) => {
    return (
        <Button variant="subtle" onClick={onClick}>
            {children}
        </Button>
    );
};

type BaseConfirmationAcceptButtonProps = {
    isLoading?: boolean;
    onClick: () => void;
} & PropsWithChildren;

const BaseConfirmationAcceptButton = ({
    children,
    isLoading,
    onClick,
}: BaseConfirmationAcceptButtonProps) => {
    const [{ focus }] = useConfirmationDialog();

    return (
        <Button
            variant={
                {
                    submit: 'primary',
                    destroy: 'danger',
                }[focus] as 'primary' | 'danger'
            }
            disabled={isLoading}
            isLoading={isLoading}
            onClick={onClick}
        >
            {children}
        </Button>
    );
};

type BaseConfirmationDialogProps = {
    icon: React.ComponentType<LucideProps>;
    title: string;
    description: React.ReactNode;
    focus?: Focus;
    className?: string;
} & PropsWithChildren;

export const BaseConfirmationDialog = ({
    icon: Icon,
    focus = 'submit',
    title,
    description,
    children,
    className,
}: BaseConfirmationDialogProps) => {
    const { visible, remove } = useDialog();

    return (
        <ConfirmationDialogProvider
            initialValue={{
                focus,
            }}
        >
            <Dialog open={visible} onClose={remove}>
                <Dialog.Header>
                    <Dialog.Icon>
                        <Icon
                            className={cn(
                                'h-4 w-4',
                                {
                                    submit: 'text-blue-500',
                                    destroy: 'text-red-600',
                                }[focus],
                            )}
                        />
                    </Dialog.Icon>
                    <Dialog.HeaderText>
                        <Dialog.Title>{title}</Dialog.Title>
                    </Dialog.HeaderText>
                </Dialog.Header>
                <Dialog.Content className={className}>
                    <Dialog.Description>{description}</Dialog.Description>
                    {children}
                </Dialog.Content>
            </Dialog>
        </ConfirmationDialogProvider>
    );
};

BaseConfirmationDialog.RejectButton = BaseConfirmationRejectButton;
BaseConfirmationDialog.AcceptButton = BaseConfirmationAcceptButton;
BaseConfirmationDialog.Actions = Dialog.Actions;

export type ConfirmationDialogProps = {
    accept: {
        text: string;
        action: () => Promise<void>;
    };
    reject: {
        text: string;
        action?: () => void;
    };
} & DialogInnerProps &
    BaseConfirmationDialogProps;

export const ConfirmationDialog = DialogStateManager.create(
    ({ icon, focus = 'submit', title, description, accept, reject, className }: ConfirmationDialogProps) => {
        const modal = useDialog();

        const [isLoading, toggleLoading] = useToggle(false);

        const handleAccept = async () => {
            toggleLoading();

            try {
                await accept.action();
            } catch (e) {
                console.warn(e);
            }

            toggleLoading();
            modal.hide();
        };

        const handleReject = () => {
            reject.action?.();
            modal.remove();
        };

        return (
            <BaseConfirmationDialog
                icon={icon}
                title={title}
                description={description}
                focus={focus}
                className={className}
            >
                <Dialog.Actions>
                    <BaseConfirmationDialog.RejectButton onClick={handleReject}>
                        {reject.text}
                    </BaseConfirmationDialog.RejectButton>
                    <BaseConfirmationDialog.AcceptButton isLoading={isLoading} onClick={handleAccept}>
                        {accept.text}
                    </BaseConfirmationDialog.AcceptButton>
                </Dialog.Actions>
            </BaseConfirmationDialog>
        );
    },
);
